import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { DeviceService } from "../../services/DeviceService";
import { useShareContext } from "../../context/ShareContext";
import { getRefetchIntervalMs } from "../../utils/refreshInterval";
import { parseJsonSafe } from "../../utils/json";

const normalizeDeviceProfile = (profile) => {
  if (!profile) {
    return null;
  }

  if (typeof profile === "object") {
    return profile;
  }

  return parseJsonSafe(profile, null);
};

// Query Keys
export const deviceKeys = {
  all: ["devices"],
  lists: () => [...deviceKeys.all, "list"],
  list: (orgId) => [...deviceKeys.lists(), orgId],
  details: () => [...deviceKeys.all, "detail"],
  detail: (id) => [...deviceKeys.details(), id],
  history: (id, startDate, endDate) => [
    ...deviceKeys.detail(id),
    "history",
    startDate,
    endDate,
  ],
  alerts: (id, startDate, endDate) => [
    ...deviceKeys.detail(id),
    "alerts",
    startDate,
    endDate,
  ],
  profile: (id) => [...deviceKeys.detail(id), "profile"],
  brands: () => [...deviceKeys.all, "brands"],
  lns: () => [...deviceKeys.all, "lns"],
};

// Query Hooks
export function useDevices(orgId) {
  const refetchIntervalMs = getRefetchIntervalMs();

  return useQuery({
    queryKey: deviceKeys.list(orgId || ""),
    queryFn: () => DeviceService.fetchDevices(orgId),
    enabled: !!orgId,
    refetchInterval: refetchIntervalMs,
  });
}

export function useDevice(deviceId) {
  // Check if we're in share context
  const shareContext = useShareContext();

  // Device details should use 5-minute default refresh, not user preference
  const refetchIntervalMs = getRefetchIntervalMs(300, 5 * 60 * 1000);

  // Always call useQuery but conditionally enable it
  const queryResult = useQuery({
    queryKey: deviceKeys.detail(deviceId || ""),
    queryFn: () => DeviceService.fetchDevice(deviceId),
    enabled: !shareContext && !!deviceId,
    refetchInterval: refetchIntervalMs,
  });

  // If we're in share mode, return the shared device
  if (shareContext) {
    return {
      data: shareContext.device || null,
      isLoading: false,
      isError: false,
      error: null,
    };
  }

  // Otherwise return the query result
  return queryResult;
}

export function useDeviceHistory(deviceId, startDate, endDate) {
  // Check if we're in share context
  const shareContext = useShareContext();

  // Get refresh interval directly from localStorage
  const refetchIntervalMs = getRefetchIntervalMs();

  // Always call useQuery but conditionally enable it
  const enabledCondition =
    !shareContext && !!(deviceId && startDate && endDate);

  const queryResult = useQuery({
    queryKey: deviceKeys.history(deviceId || "", startDate || 0, endDate || 0),
    queryFn: () =>
      DeviceService.fetchDeviceHistory(deviceId, startDate, endDate),
    enabled: enabledCondition,
    refetchInterval: refetchIntervalMs,
    staleTime: refetchIntervalMs || 5 * 60 * 1000, // Keep data fresh until next refetch
    refetchIntervalInBackground: true, // Continue refreshing even when tab is not focused
    placeholderData: keepPreviousData, // Keep previous data while fetching new date range
  });

  // If we're in share mode, return the shared device history
  if (shareContext) {
    return {
      data: shareContext.deviceHistory || [],
      isLoading: false,
      isError: false,
      error: null,
    };
  }

  // Otherwise return the query result
  return queryResult;
}

export function useDeviceAlerts(deviceId, startDate, endDate) {
  const refetchIntervalMs = getRefetchIntervalMs();

  return useQuery({
    queryKey: deviceKeys.alerts(deviceId || "", startDate || 0, endDate || 0),
    queryFn: () =>
      DeviceService.fetchDeviceAlerts(deviceId, startDate, endDate),
    enabled: !!(deviceId && startDate && endDate),
    refetchInterval: refetchIntervalMs,
    staleTime: refetchIntervalMs || 5 * 60 * 1000, // Keep data fresh until next refetch
    refetchIntervalInBackground: true, // Continue refreshing even when tab is not focused
    placeholderData: keepPreviousData, // Keep previous data while fetching new date range
  });
}

export function useDeviceProfile(deviceId) {
  // Check if we're in share context
  const shareContext = useShareContext();

  // Device profile should use 5-minute default refresh, not user preference
  const refetchIntervalMs = getRefetchIntervalMs(300, 5 * 60 * 1000);

  // Always call useQuery but conditionally enable it
  const queryResult = useQuery({
    queryKey: deviceKeys.profile(deviceId || ""),
    queryFn: () => DeviceService.fetchDeviceProfile(deviceId),
    enabled: !shareContext && !!deviceId,
    refetchInterval: refetchIntervalMs,
    select: normalizeDeviceProfile,
  });

  // If we're in share mode, return the shared device profile
  if (shareContext) {
    return {
      data: normalizeDeviceProfile(shareContext.deviceProfile),
      isLoading: false,
      isError: false,
      error: null,
    };
  }

  // Otherwise return the query result
  return queryResult;
}

export function useDeviceBrands() {
  return useQuery({
    queryKey: deviceKeys.brands(),
    queryFn: DeviceService.fetchDeviceBrands,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useLNS() {
  return useQuery({
    queryKey: deviceKeys.lns(),
    queryFn: DeviceService.fetchLNS,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Mutation Hooks
export function useUpdateDeviceName() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ device_id, device_name }) =>
      DeviceService.changeDeviceName(device_id, device_name),
    onSuccess: (_, variables) => {
      // Invalidate and refetch device details
      queryClient.invalidateQueries({
        queryKey: deviceKeys.detail(variables.device_id),
      });
      // Invalidate all device lists
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
}

export function useUpdateDeviceXY() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deviceID, x, y }) =>
      DeviceService.updateDeviceXY(deviceID, x, y),
    onSuccess: (_, variables) => {
      // Invalidate device details
      queryClient.invalidateQueries({
        queryKey: deviceKeys.detail(variables.deviceID),
      });
      // Invalidate all device lists
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
}

export function useClaimDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ claimCode, organisationId }) =>
      DeviceService.claimDevice(claimCode, organisationId),
    onSuccess: (_, variables) => {
      // Invalidate all device lists for the organization
      queryClient.invalidateQueries({
        queryKey: deviceKeys.list(variables.organisationId),
      });
    },
  });
}

export function useSelfHostDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: DeviceService.selfHostDevice,
    onSuccess: (_, variables) => {
      // Invalidate all device lists for the organization
      queryClient.invalidateQueries({
        queryKey: deviceKeys.list(variables.organisation_id),
      });
    },
  });
}

export function useDeleteDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: DeviceService.deleteDevice,
    onSuccess: (_, deviceId) => {
      // Invalidate all device lists
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
      // Remove the specific device from cache
      queryClient.removeQueries({ queryKey: deviceKeys.detail(deviceId) });
    },
  });
}

export function useTransferDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deviceId, organisationId }) =>
      DeviceService.transferDevice(deviceId, organisationId),
    onSuccess: (_, { deviceId }) => {
      // Invalidate all device lists for all organizations
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
      // Remove the specific device from cache as it moved organizations
      queryClient.removeQueries({ queryKey: deviceKeys.detail(deviceId) });
    },
  });
}

export function useUpdateDeviceProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deviceId, profile }) =>
      DeviceService.updateDeviceProfile({ deviceId, profile }),
    onSuccess: (_, { deviceId }) => {
      // Invalidate the device profile query to refetch updated data
      queryClient.invalidateQueries({
        queryKey: deviceKeys.profile(deviceId),
      });
    },
  });
}

export function useAddDeviceManually() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deviceData) => DeviceService.addDeviceManually(deviceData),
    onSuccess: (_, { organisationId }) => {
      // Invalidate device lists for the organization where device was added
      queryClient.invalidateQueries({
        queryKey: deviceKeys.list(organisationId),
      });
      // Also invalidate all device lists to catch any summary changes
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
}

export function useSendDownlink() {
  return useMutation({
    mutationFn: ({ deviceId, command, port }) =>
      DeviceService.sendDownlink({ deviceId, command, port }),
  });
}

export function useSendCat1mDownlink() {
  return useMutation({
    mutationFn: ({ deviceId, command }) =>
      DeviceService.sendCat1mDownlink({ deviceId, command }),
  });
}

export function useDeleteFrame() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deviceId, fcnt, timeCreated }) =>
      DeviceService.deleteFrame({ deviceId, fcnt, timeCreated }),
    onSuccess: (_) => {
      // Invalidate device history to refresh after frame deletion
      queryClient.invalidateQueries({
        queryKey: deviceKeys.details(),
      });
    },
  });
}

export function useShareDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deviceId) => DeviceService.shareDevice(deviceId),
    onSuccess: (_, deviceId) => {
      // Invalidate the specific device to refresh sharing status
      queryClient.invalidateQueries({
        queryKey: deviceKeys.detail(deviceId),
      });
    },
  });
}

// Share-specific hooks for public access via share ID
export function useSharedDevice(shareId, options = {}) {
  return useQuery({
    queryKey: ["sharedDevice", shareId],
    queryFn: () => DeviceService.fetchSharedDevice(shareId),
    enabled: !!shareId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

export function useSharedDeviceHistory(
  shareId,
  startDate,
  endDate,
  options = {}
) {
  const refetchIntervalMs = getRefetchIntervalMs();

  return useQuery({
    queryKey: ["sharedDeviceHistory", shareId, startDate, endDate],
    queryFn: () =>
      DeviceService.fetchSharedDeviceHistory(shareId, startDate, endDate),
    enabled: !!shareId && startDate !== undefined && endDate !== undefined,
    refetchInterval: refetchIntervalMs,
    staleTime: 30 * 1000, // 30 seconds
    ...options,
  });
}

export function useSharedDeviceProfile(shareId, options = {}) {
  return useQuery({
    queryKey: ["sharedDeviceProfile", shareId],
    queryFn: () => DeviceService.fetchSharedDeviceProfile(shareId),
    enabled: !!shareId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}
